import { ExpandLess, ExpandMore, Folder, FolderOpen } from '@mui/icons-material';
import { Box, Collapse, Typography } from '@mui/material';
import { useState } from 'react';
import CurriculumItem from '@/components/curriculum/curriculum-item';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Chapter } from '@/types';
import { useRoute } from '@/utils/client/route';

export default function CurriculumTableItem({
  title,
  children,
  hasMembership,
  isOpen = false,
  sectionChapter,
}: {
  title: string;
  children: Chapter[];
  hasMembership: boolean;
  isOpen?: boolean;
  sectionChapter?: Chapter;
}) {
  const [open, setOpen] = useState(isOpen);
  const { goToRoute } = useRoute();
  const { selectedCourse } = useCourses();
  const { toggleMenuDrawer, showMenuDrawer } = useViewStore();

  const onToggle = () => setOpen(!open);

  const onNavigate = () => {
    if (sectionChapter?.item?.slug && selectedCourse?.slug) {
      goToRoute('myCourseLearn', {
        courseSlug: selectedCourse.slug,
        chapterSlug: sectionChapter.item.slug,
      });

      if (showMenuDrawer) toggleMenuDrawer();
    }
  };

  return (
    <Box>
      <Box
        sx={(theme) => ({
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          mt: 0.25,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[2],
          },
        })}>
        {/* Left side - Navigable area (folder icon + title) */}
        <Box
          onClick={sectionChapter ? onNavigate : undefined}
          sx={(theme) => ({
            display: 'flex',
            gap: children.length > 0 ? 1.5 : 0,
            alignItems: 'center',
            flex: 1,
            padding: { xs: '12px 8px 12px 8px', sm: '12px 12px 12px 16px' },
            cursor: sectionChapter ? 'pointer' : 'default',
            borderRadius: children.length > 0 ? '4px 0 0 4px' : '4px',
            '&:hover': sectionChapter
              ? {
                  backgroundColor: theme.palette.action.hover,
                }
              : {},
          })}>
          {children.length > 0 && (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                minWidth: 20,
              }}>
              {open ? <FolderOpen color="primary" /> : <Folder color="action" />}
            </Box>
          )}
          <Typography
            sx={{
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: '1rem',
            }}>
            {title}
          </Typography>
        </Box>

        {/* Right side - Collapse toggle area */}
        {children.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              onClick={onToggle}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                opacity: 0.7,
                cursor: 'pointer',
                padding: {
                  xs: '12px 12px 12px 8px',
                  sm: '12px 20px 12px 12px',
                },
                borderRadius: '0 4px 4px 0',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  opacity: 1,
                },
              })}>
              <Typography variant="body1" sx={{ fontSize: 11, mr: 1 }}>
                {children.length} Kapitel
              </Typography>
              {open ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </Box>
        )}
      </Box>
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderTop: 0,
          borderRadius: '0 0 4px 4px',
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.divider,
        })}>
        <Box sx={{ py: 1 }}>
          {children.map((chapter: Chapter) => (
            <CurriculumItem
              key={chapter.id}
              chapter={chapter}
              hasMembership={hasMembership}
              indentation={true}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
